import { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import {
  CONNECTOR_CATEGORY_LABELS,
  CONNECTOR_CATEGORY_ORDER,
  ConnectorCategory,
  ConnectorListItem,
  ConnectorStatus,
} from '@optidev/shared-types';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import { ConnectorIcon } from './ConnectorIcon';

/**
 * Picking a source to connect, as one self-contained panel.
 *
 * The page used to render a bordered card per category, each wrapping a three-column grid. With 41
 * connectors across 17 categories, nine of which hold exactly one, that produced seventeen nested
 * containers and a very long page. Here category is navigation *inside* a bounded panel, so the
 * panel is the same size whether a category holds one connector or eleven, and the page never grows.
 */

/** Categories with fewer than this many connectors are merged, rather than each getting an entry. */
const MIN_FOR_OWN_CATEGORY = 2;
const OTHER = '__other__';

interface SourcePickerProps {
  connectors: ConnectorListItem[];
  /** Connector ids that already have at least one connection, shown as such instead of "Connect". */
  connectedIds: Set<string>;
  onPick: (connectorId: string) => void;
  isLoading?: boolean;
  className?: string;
  /** Changes when the empty state is clicked; moves focus here. */
  focusSearchSignal?: number;
}

interface Group {
  key: string;
  label: string;
  items: ConnectorListItem[];
}

export function SourcePicker({
  connectors,
  connectedIds,
  onPick,
  isLoading = false,
  className,
  focusSearchSignal,
}: SourcePickerProps): React.ReactElement {
  const searchRef = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState<string>('all');
  const [query, setQuery] = useState('');

  // Skipped on first render: focus should follow a click, not grab the caret on arrival.
  useEffect(() => {
    if (focusSearchSignal) searchRef.current?.focus();
  }, [focusSearchSignal]);

  const usable = useMemo(
    () => connectors.filter((c) => c.status !== ConnectorStatus.DEPRECATED),
    [connectors],
  );

  /**
   * Build the category list, folding the long tail into one entry. Nine categories holding a single
   * connector is the same sprawl the old layout had, just moved into the sidebar.
   */
  const groups = useMemo<Group[]>(() => {
    const byCategory = new Map<ConnectorCategory, ConnectorListItem[]>();
    const tail: ConnectorListItem[] = [];

    for (const cat of CONNECTOR_CATEGORY_ORDER) {
      const items = usable.filter((c) => c.category === cat);
      if (items.length >= MIN_FOR_OWN_CATEGORY) byCategory.set(cat, items);
      else tail.push(...items);
    }

    // Anything whose category isn't in the known list still has to appear somewhere.
    const known = new Set(CONNECTOR_CATEGORY_ORDER as string[]);
    tail.push(...usable.filter((c) => !known.has(c.category)));

    const result: Group[] = [...byCategory.entries()].map(([cat, items]) => ({
      key: cat,
      label: CONNECTOR_CATEGORY_LABELS[cat],
      items,
    }));
    if (tail.length) result.push({ key: OTHER, label: 'Everything else', items: tail });
    return result;
  }, [usable]);

  /**
   * Search wins over the selected category, so someone who knows the name never has to guess which
   * category it lives in.
   */
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const pool = needle
      ? usable
      : category === 'all'
        ? usable
        : (groups.find((g) => g.key === category)?.items ?? []);

    const matched = needle
      ? pool.filter(
          (c) =>
            c.name.toLowerCase().includes(needle) ||
            c.description?.toLowerCase().includes(needle),
        )
      : pool;

    // Available first, then alphabetical, so nothing usable sits below a coming-soon entry.
    return [...matched].sort((a, b) => {
      const aReady = a.status === ConnectorStatus.AVAILABLE ? 0 : 1;
      const bReady = b.status === ConnectorStatus.AVAILABLE ? 0 : 1;
      return aReady - bReady || a.name.localeCompare(b.name);
    });
  }, [category, groups, query, usable]);

  const isSearching = query.trim().length > 0;

  return (
    <div
      className={cn(
        // Fills the height handed down by the page: a fixed height left a large dead area below on
        // any normal screen. The list still scrolls internally, so the page itself never grows.
        'flex flex-col overflow-hidden rounded-xl border border-border bg-card',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-3 border-b border-border bg-muted/30 px-4 py-3">
        <h2 className="text-sm font-semibold tracking-tight">Add a data source</h2>
        <span className="text-xs text-muted-foreground">
          {visible.length === usable.length
            ? `${usable.length} sources`
            : `${visible.length} of ${usable.length}`}
        </span>
        <div className="relative ml-auto w-full max-w-[240px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search sources"
            className="h-8 pl-8 text-xs"
          />
        </div>
      </div>

      {/* Scrolling happens inside, so the panel is the same size whatever the category holds. */}
      <div className="grid min-h-0 flex-1 grid-cols-1 sm:grid-cols-[164px_1fr]">
        <nav
          aria-label="Source categories"
          className="flex gap-1 overflow-x-auto border-b border-border bg-muted/20 p-2 sm:flex-col sm:overflow-y-auto sm:overflow-x-hidden sm:border-b-0 sm:border-r"
        >
          <CategoryButton
            label="All sources"
            count={usable.length}
            isActive={category === 'all' && !isSearching}
            onClick={() => {
              setCategory('all');
              setQuery('');
            }}
          />
          {groups.map((group) => (
            <CategoryButton
              key={group.key}
              label={group.label}
              count={group.items.length}
              isActive={category === group.key && !isSearching}
              onClick={() => {
                setCategory(group.key);
                setQuery('');
              }}
            />
          ))}
        </nav>

        <div className="overflow-y-auto">
          {isLoading && (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground">
              Loading sources&hellip;
            </p>
          )}

          {!isLoading && visible.length === 0 && (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground">
              {isSearching ? `Nothing matches “${query.trim()}”.` : 'Nothing here yet.'}
            </p>
          )}

          {!isLoading &&
            visible.map((connector) => {
              const isReady = connector.status === ConnectorStatus.AVAILABLE;
              const isConnected = connectedIds.has(connector.id);

              return (
                <button
                  key={connector.id}
                  type="button"
                  /*
                    The row's accessible name ends in "Connect" / "Connected" / "Soon", so it
                    changes with state, and nine connectors are named "Google …" — neither an
                    exact nor a substring name selector is stable. The id is.
                  */
                  data-testid={`source-${connector.id}`}
                  disabled={!isReady}
                  onClick={() => onPick(connector.id)}
                  className={cn(
                    'group flex w-full items-center gap-3.5 border-b border-border/60 px-4 py-3 text-left transition-colors last:border-0',
                    isReady ? 'hover:bg-accent/60' : 'cursor-default opacity-45',
                  )}
                >
                  {/*
                    The real service mark, on a tile: logos vary wildly in shape and aspect, and a
                    bare <img> makes the rows look ragged. The tile gives every row the same anchor.
                  */}
                  <span className="grid size-9 flex-none place-items-center rounded-lg border border-border bg-background p-1.5">
                    <ConnectorIcon connector={connector} className="size-full" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold tracking-tight">
                      {connector.name}
                    </span>
                    {connector.description && (
                      <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                        {connector.description}
                      </span>
                    )}
                  </span>
                  <span className="flex-none">
                    {!isReady ? (
                      <span className="text-xs font-medium text-muted-foreground">Soon</span>
                    ) : isConnected ? (
                      <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                        Connected
                      </span>
                    ) : (
                      <span className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition-colors group-hover:border-primary group-hover:text-primary">
                        Connect
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
        </div>
      </div>
    </div>
  );
}

function CategoryButton({
  label,
  count,
  isActive,
  onClick,
}: {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={isActive ? 'true' : undefined}
      className={cn(
        'flex flex-none items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium transition-colors sm:flex-initial',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      <span className="whitespace-nowrap">{label}</span>
      <span className={cn('ml-auto tabular-nums', isActive ? 'opacity-80' : 'opacity-60')}>
        {count}
      </span>
    </button>
  );
}
